import { HTMLProps, useState } from "react";
import { t } from "@lingui/core/macro";

import { Icon } from "_/components/icon";

import * as S from "./styled";

/**
 * Component to show a code sample, with a button to copy the code to the clipboard.
 *
 * Note that this component is similar to components/docs-view/code.tsx and
 * components/uuid-tooltip/index.tsx, and at some point we should unify them.
 */
export const CopyableCode = (
  props: Omit<HTMLProps<HTMLDivElement>, "children"> & {
    children: string | undefined;
  }
) => {
  const { children, ...rest } = props;
  const [copyState, setCopyState] = useState<
    "notAttempted" | "copied" | "error"
  >("notAttempted");
  const code = props.children;
  if (!code) return null;

  async function copyToClipboard(toCopy: string) {
    try {
      await navigator.clipboard.writeText(toCopy);
      setCopyState("copied");
      setTimeout(() => setCopyState("notAttempted"), 3000);
    } catch {
      setCopyState("error");
      console.error("Could not copy code to clipboard.");
    }
  }

  const iconVariant = (
    {
      notAttempted: "ContentCopy",
      error: "Error",
      copied: "CheckCircle",
    } as const
  )[copyState];

  // The icon is the only visible content, so the label carries the outcome
  // for screen readers as well as the action.
  const label = {
    notAttempted: t`components.copyable-code.copy`,
    error: t`components.copyable-code.copy-failed`,
    copied: t`components.copyable-code.copied`,
  }[copyState];

  return (
    <S.Container {...rest}>
      <S.ContentWrapper>{children}</S.ContentWrapper>
      <S.CopyButtonWrapper>
        <S.CopyButton
          type="button"
          aria-label={label}
          aria-live="polite"
          onClick={() => void copyToClipboard(code)}
        >
          <Icon variant={iconVariant} />
        </S.CopyButton>
      </S.CopyButtonWrapper>
    </S.Container>
  );
};
